import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetApplicationsQuery,
  useUpdateApplicationMutation,
  useDeleteApplicationMutation,
} from '../components/applicationApiSlice'; // Adjust the path if needed
import {
  Box,
  TextField,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Typography,
  Card,
  CardContent,
  CardMedia,
} from '@mui/material';
import ImageModal from '../components/ImageModal'; // Import the ImageModal component

const ApplicationEdit = () => {
  const { id } = useParams(); // Get the application ID from the URL
  const navigate = useNavigate(); // Navigate after update or delete
  
  // Query to fetch all applications
  const { data: applications, isLoading, isError } = useGetApplicationsQuery();
  
  // Mutation hooks for update and delete
  const [updateApplication] = useUpdateApplicationMutation();
  const [deleteApplication] = useDeleteApplicationMutation();
  
  // State for the editable form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    line: '',
    facebook: '',
    age: '',
    grade: '',
    reason: '',
  });

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [modalImage, setModalImage] = useState('');

  useEffect(() => {
    if (applications?.entities?.[id]) {
      const application = applications.entities[id];
      setFormData({
        name: application.name || '',
        email: application.email || '',
        phone: application.phone || '',
        line: application.line || '',
        facebook: application.facebook || '',
        age: application.age || '',
        grade: application.grade || '',
        reason: application.reason || '',
      });
    }
  }, [applications, id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    try {
      await updateApplication({ id, updatedItem: formData });
      navigate('/applications'); // Redirect to the list after save
    } catch (err) {
      console.error('Error updating application:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteApplication(id);
      navigate('/applications'); // Redirect to the list after delete
    } catch (err) {
      console.error('Error deleting application:', err);
    }
  };

  const handleImageClick = (image) => {
    setModalImage(image);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalImage('');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Alert severity="error">Error fetching application details.</Alert>
      </Box>
    );
  }

  const application = applications.entities[id];

  return (
    <Box padding={2} bgcolor="#f5f5f5">
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ boxShadow: 3, borderRadius: 2, marginBottom: 2 }}>
            <CardMedia
              component="img"
              image={application.photo?.link}
              alt={`${application.name}'s photo`}
              sx={{ height: 300, cursor: 'pointer' }}
              onClick={() => handleImageClick(application.photo?.link)}
            />
            <CardContent>
              <Typography variant="h5" gutterBottom>
                {application.name}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <CardMedia
              component="img"
              image={application.reportCard?.link}
              alt={`${application.name}'s report card`}
              sx={{ height: 300, cursor: 'pointer' }}
              onClick={() => handleImageClick(application.reportCard?.link)}
            />
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Report Card
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={8}>
          <Box>
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Line"
              name="line"
              value={formData.line}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Facebook"
              name="facebook"
              value={formData.facebook}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Age"
              name="age"
              value={formData.age}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Grade"
              name="grade"
              value={formData.grade}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />
            <TextField
              label="Reason"
              name="reason"
              value={formData.reason}
              onChange={handleChange}
              fullWidth
              margin="normal"
            />

            <Box display="flex" justifyContent="space-between" marginTop={2}>
              <Button variant="outlined" color="secondary" onClick={handleDelete}>
                Delete
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Fullscreen Image Modal */}
      <ImageModal src={modalImage} open={openModal} onClose={handleCloseModal} />
    </Box>
  );
};

export default ApplicationEdit;
